<script setup>
import { ref, computed, onMounted, watch } from "vue";
import * as bootstrap from "bootstrap";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { usePlanStore } from "@/stores/planStore";
import { useCompanyStore } from "@/stores/companyStore";

const props = defineProps({
  subscription: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["created"]);

const subscriptionStore = useSubscriptionStore();
const planStore = usePlanStore();
const companyStore = useCompanyStore();

const form = ref({
  company_id: "",
  plan_id: "",
  starts_at: "",           // date
  payment_status: "paid",  // paid | unpaid | refunded
  payment_method: "Manual",
  status: "active",
  license_expires_at: "",
  grace_period_ends_at: "",
  grace_period_months: "",
});

const todayStr = () => new Date().toISOString().slice(0, 10);

const selectedPlan = computed(() =>
  planStore.plans?.find((p) => p.id === Number(form.value.plan_id)) || null
);
const isEditMode = computed(() => !!props.subscription?.id);
const modalTitle = computed(() =>
  isEditMode.value ? "Edit Subscription" : "Add Subscription"
);

const companies = computed(() => companyStore.companies || []); // adapt to your store
const activePlans = computed(() =>
  (planStore.plans || []).filter((p) => p.status === "active")
);

onMounted(async () => {
  // default values
  form.value.starts_at = todayStr();

  // load companies & plans if not already
  if (!companies.value.length && companyStore.fetchCompanies) {
    await companyStore.fetchCompanies({ all: true });
  }
  if (!planStore.plans?.length && planStore.fetchPlans) {
    await planStore.fetchPlans({ status: "active" });
  }
});

const resetForm = () => {
  form.value = {
    company_id: "",
    plan_id: "",
    starts_at: todayStr(),
    payment_status: "paid",
    payment_method: "Manual",
    status: "active",
    license_expires_at: "",
    grace_period_ends_at: "",
    grace_period_months: "",
  };
};

const populateForm = (subscription) => {
  if (!subscription) {
    resetForm();
    return;
  }

  form.value = {
    company_id: subscription.company_id ? String(subscription.company_id) : "",
    plan_id: subscription.plan_id ? String(subscription.plan_id) : "",
    starts_at: subscription.starts_at || todayStr(),
    payment_status: subscription.payment_status || "paid",
    payment_method: subscription.payment_method || "Manual",
    status: subscription.status || "active",
    license_expires_at: subscription.license_expires_at || "",
    grace_period_ends_at: subscription.grace_period_ends_at || "",
    grace_period_months:
      subscription.grace_period_months != null
        ? String(subscription.grace_period_months)
        : "",
  };
};

const hideModal = () => {
  const el = document.getElementById("add_subscription");

  if (el) {
    // Ensure we always have an instance
    let instance = bootstrap.Modal.getInstance(el);
    if (!instance) {
      instance = bootstrap.Modal.getOrCreateInstance(el);
    }
    instance.hide();
  }

  // --- Safety cleanup: remove leftover backdrop & body class ---
  setTimeout(() => {
    document.body.classList.remove("modal-open");
    const backdrops = document.querySelectorAll(".modal-backdrop");
    backdrops.forEach((bd) => bd.remove());
  }, 300); // wait a bit for Bootstrap animation
};


const handleSubmit = async () => {
  if (!form.value.company_id || !form.value.plan_id) return;

  try {
    if (isEditMode.value) {
      await subscriptionStore.updateSubscription(props.subscription.id, {
        starts_at: form.value.starts_at || null,
        payment_status: form.value.payment_status,
        payment_method: form.value.payment_method || null,
        status: form.value.status,
        license_expires_at: form.value.license_expires_at || null,
        grace_period_ends_at: form.value.grace_period_ends_at || null,
        grace_period_months:
          form.value.grace_period_months === ""
            ? null
            : Number(form.value.grace_period_months),
      });
    } else {
      const payload = {
        company_id: form.value.company_id,
        plan_id: form.value.plan_id,
        starts_at: form.value.starts_at,
        payment_status: form.value.payment_status,
        payment_method: form.value.payment_method || null,
      };
      await subscriptionStore.createSubscription(payload);
    }
    hideModal();
    resetForm();
    emit("created");
  } catch (e) {
    // toast already handled
  }
};

watch(
  () => props.subscription,
  (value) => {
    populateForm(value);
  },
  { immediate: true }
);
</script>

<template>
  <!-- Add Subscription -->
  <div class="modal fade" id="add_subscription" tabindex="-1" @hidden.bs.modal="resetForm">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ modalTitle }}</h4>
          <button
            type="button"
            class="btn-close custom-btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <div class="row">
              <!-- Company -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">
                    Company<span class="text-danger"> *</span>
                  </label>
                  <select
                    class="form-select"
                    v-model="form.company_id"
                    :disabled="isEditMode"
                    required
                  >
                    <option value="">Select Company</option>
                    <option
                      v-for="c in companies"
                      :key="c.id"
                      :value="c.id"
                    >
                      {{ c.company_name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Plan -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">
                    Plan<span class="text-danger"> *</span>
                  </label>
                  <select
                    class="form-select"
                    v-model="form.plan_id"
                    :disabled="isEditMode"
                    required
                  >
                    <option value="">Select Plan</option>
                    <option
                      v-for="p in activePlans"
                      :key="p.id"
                      :value="p.id"
                    >
                      {{ p.name }} -
                      {{
                        p.is_contact_sales
                          ? "Contact Sales"
                          : `${p.currency} ${Number(p.price || 0).toFixed(2)}`
                      }}
                    </option>
                  </select>
                  <small v-if="selectedPlan" class="text-muted">
                    Trial:
                    <span v-if="selectedPlan.allow_trial">
                      {{ selectedPlan.trial_interval_value || selectedPlan.trial_days }}
                      {{ selectedPlan.trial_interval_unit || "days" }}
                    </span>
                    <span v-else>no trial</span>
                    ,
                    annual licence fee:
                    {{ selectedPlan.currency }}
                    {{ Number(selectedPlan.license_fee || 0).toFixed(2) }}
                  </small>
                </div>
              </div>

              <!-- Starts at -->
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Start Date</label>
                  <input
                    type="date"
                    class="form-control"
                    v-model="form.starts_at"
                  />
                </div>
              </div>

              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Plan Price</label>
                  <input
                    type="text"
                    class="form-control"
                    :value="
                      selectedPlan
                        ? selectedPlan.is_contact_sales
                          ? 'Contact Sales'
                          : `${selectedPlan.currency} ${Number(selectedPlan.price || 0).toFixed(2)}`
                        : ''
                    "
                    readonly
                    placeholder="Auto from plan"
                  />
                </div>
              </div>

              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Annual Licence Fee</label>
                  <input
                    type="text"
                    class="form-control"
                    :value="
                      selectedPlan
                        ? `${selectedPlan.currency} ${Number(selectedPlan.license_fee || 0).toFixed(2)}`
                        : ''
                    "
                    readonly
                  />
                </div>
              </div>

              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Payment Status</label>
                  <select
                    class="form-select"
                    v-model="form.payment_status"
                  >
                    <option value="paid">Paid</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="refunded">Refunded</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              <!-- Payment method -->
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Payment Method</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="form.payment_method"
                    placeholder="E.g. Mpesa, Credit Card, Bank Transfer"
                  />
                </div>
              </div>

              <template v-if="isEditMode">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Access Status</label>
                    <select class="form-select" v-model="form.status">
                      <option value="trialing">Trialing</option>
                      <option value="active">Active</option>
                      <option value="grace_period">Grace Period</option>
                      <option value="locked">Locked</option>
                      <option value="suspended">Suspended</option>
                      <option value="expired">Expired</option>
                      <option value="canceled">Canceled</option>
                    </select>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">License Expiry</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="form.license_expires_at"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Grace Period End</label>
                    <input
                      type="date"
                      class="form-control"
                      v-model="form.grace_period_ends_at"
                    />
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Grace Period Months</label>
                    <input
                      type="number"
                      min="0"
                      max="24"
                      class="form-control"
                      v-model="form.grace_period_months"
                    />
                  </div>
                </div>
              </template>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-light me-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button type="submit" class="btn btn-primary">
              <span
                v-if="subscriptionStore.loadingForCreate"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              {{ isEditMode ? "Save Changes" : "Create Subscription" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
